import { Modal, Group, Button, Checkbox, Text, TextInput, Badge, Skeleton, Stack, Box, useMantineTheme } from '@mantine/core';
import { IconCheck, IconX, IconCopy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { notifications } from '@mantine/notifications';
import FunnelApi from "../../api/FunnelApi";

const activeFeatureTags = {
	test: 'ACTIVE TEST — copies over, locked while the test runs',
	draft_test: 'DRAFT TEST — copies over, locked while the draft exists',
	campaign: 'ACTIVE CAMPAIGN — copies over, locked while the campaign runs',
	heatmap: 'ACTIVE HEATMAP — copies over, locked while the heatmap runs',
	scrollmap: 'ACTIVE SCROLLMAP — copies over, locked while the scrollmap runs',
};

export function DuplicateFunnel({duplicateFunnelModalOpen, setDuplicateFunnelModalOpen, selectedMemberWebsite, SelectedFunnelValue, setUpdatedtime, handleSelectFunnelListComponentChange}:any){

	const navigate = useNavigate();
	const theme = useMantineTheme();
	const dark = theme.colorScheme === 'dark';
	const primary = theme.colors[theme.primaryColor];
	const [newFunnelName, setNewFunnelName] = useState('');
	const [nameError, setNameError] = useState('');
	const [funnelPages, setFunnelPages] = useState([]);
	const [checkedPages, setCheckedPages] = useState<string[]>([]);
	const [pagesLoading, setPagesLoading] = useState(false);
	const [duplicating, setDuplicating] = useState(false);

	useEffect(() => {
		if(duplicateFunnelModalOpen && SelectedFunnelValue){
			setFunnelPages([]);
			setCheckedPages([]);
			setNewFunnelName('');
			setNameError('');
			setPagesLoading(true);
			FunnelApi.getduplicatefunnelpages({
				system_website_id: selectedMemberWebsite,
				funnel_id: SelectedFunnelValue
			})
			.then(response => {
				if (response.ok) {
					return response.json();
				} else {
					if (response.status === 401 || response.status === 400) {
						navigate("/logout");
					}
				}
			})
			.then(data => {
				if (data && data.status === 1) {
					setNewFunnelName(data.data.new_funnel_name);
					setFunnelPages(data.data.pages);
					setCheckedPages(data.data.pages.map((page:any) => page.funnel_page_id));
				} else if (data) {
					notifications.show({
						color: 'red',
						icon: <IconX size="1.1rem" />,
						title: 'Error',
						message: data.message,
					});
					setDuplicateFunnelModalOpen(false);
				}
				setPagesLoading(false);
			})
			.catch(error => {
				console.error("API Error:", error);
				setPagesLoading(false);
			});
		}
	}, [duplicateFunnelModalOpen]);

	const handleDuplicateFunnelCloseModal = () => {
		if (!duplicating) {
			setDuplicateFunnelModalOpen(false);
		}
	};

	const togglePage = (funnelPageId:string) => {
		setCheckedPages(prev => prev.includes(funnelPageId) ? prev.filter(id => id !== funnelPageId) : [...prev, funnelPageId]);
	};

	const handleDuplicateFunnelSubmit = async () => {
		if (newFunnelName.trim() === '') {
			setNameError('Name is Required field');
			return;
		}
		setNameError('');
		setDuplicating(true);
		try {
			let no_of_funnels_access;
			const userData = JSON.parse(atob(localStorage.getItem('visiUserData')));
			if (userData && userData.no_of_add_access) {
				no_of_funnels_access = userData.no_of_add_access.no_of_funnels_access;
			}
			const APIResponse = await FunnelApi.duplicatefunnel({
				system_website_id: selectedMemberWebsite,
				funnel_id: SelectedFunnelValue,
				name: newFunnelName.trim(),
				funnel_page_ids: checkedPages,
				account_funnel_limit: no_of_funnels_access
			});
			const APIResultJSON = await APIResponse.json();
			if (APIResultJSON.status === 1) {
				notifications.show({
					color: 'green',
					icon: <IconCheck size="1.1rem" />,
					title: 'Success',
					message: APIResultJSON.message,
				});
				setDuplicateFunnelModalOpen(false);
				setUpdatedtime(APIResultJSON.data.selected_funnel_time);
				handleSelectFunnelListComponentChange(APIResultJSON.data.selected_funnel_id);
			} else if (['name_exists', 'name_invalid'].includes(APIResultJSON.error_code)) {
				setNameError(APIResultJSON.message);
			} else {
				notifications.show({
					color: 'red',
					icon: <IconX size="1.1rem" />,
					title: 'Error',
					message: APIResultJSON.message,
				});
			}
		} catch (error) {
			console.log('error', error);
		}
		setDuplicating(false);
	};

	return (
		<Modal
			opened={duplicateFunnelModalOpen}
			onClose={handleDuplicateFunnelCloseModal}
			radius="lg"
			size={600}
			padding="xl"
			trapFocus={false}
			title={
				<Group spacing={10} noWrap>
					<Box style={{ width: 32, height: 32, borderRadius: 8, display: 'grid', placeItems: 'center', background: dark ? theme.fn.rgba(primary[8], 0.25) : primary[0], color: dark ? primary[3] : primary[7] }}>
						<IconCopy size={18} />
					</Box>
					<Text fw={600} fz={16}>Duplicate Funnel</Text>
				</Group>
			}
		>
			<Text fz={14} c="dimmed" lh={1.6} mb={18}>
				Pick the pages to copy into the new funnel. Untick any page you plan to replace. Pages with an active feature stay in the new funnel until that feature is stopped and you remove them manually.
			</Text>
			{pagesLoading ? (
				<Stack spacing="sm">
					<Skeleton height={16} width="40%" radius="sm" />
					<Skeleton height={68} radius="md" />
					<Skeleton height={68} radius="md" />
				</Stack>
			) : (
				<>
					<TextInput
						label="New funnel name"
						value={newFunnelName}
						onChange={(event) => {
							setNewFunnelName(event.currentTarget.value);
							setNameError('');
						}}
						error={nameError}
						maxLength={255}
						radius="md"
						size="md"
						mb={18}
						disabled={duplicating}
					/>
					<Text fz={12} fw={700} c="dimmed" mb={8} style={{ letterSpacing: '0.06em' }}>PAGES TO INCLUDE ({checkedPages.length}/{funnelPages.length})</Text>
					{funnelPages.length === 0 ? (
						<Text fz={14} c="dimmed">This funnel has no pages to duplicate.</Text>
					) : (
						<Stack spacing={10} style={{ maxHeight: 360, overflowY: 'auto' }}>
							{funnelPages.map((page:any) => {
								const checked = checkedPages.includes(page.funnel_page_id);
								return (
									<Box
										key={page.funnel_page_id}
										component="label"
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 14,
											padding: '12px 16px',
											borderRadius: 12,
											cursor: duplicating ? 'default' : 'pointer',
											background: dark ? theme.colors.dark[6] : theme.colors.gray[0],
											border: `1px solid ${checked ? (dark ? primary[7] : primary[3]) : (dark ? theme.colors.dark[4] : theme.colors.gray[3])}`,
											opacity: checked ? 1 : 0.65,
										}}
									>
										<Checkbox checked={checked} onChange={() => togglePage(page.funnel_page_id)} disabled={duplicating} size="md" radius="sm"/>
										<Box style={{ flex: 1, minWidth: 0 }}>
											<Text fz={15} fw={600} truncate>{page.title || page.url}</Text>
											<Text fz={12.5} c="dimmed" style={{ fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-all' }}>
												{page.url}
											</Text>
										</Box>
										{activeFeatureTags[page.active_feature] && (
											<Badge color="orange" variant="light" radius="sm" maw={220} styles={{ root: { height: 'auto', paddingTop: 4, paddingBottom: 4, flexShrink: 0 }, inner: { whiteSpace: 'normal', textAlign: 'center' } }}>
												{activeFeatureTags[page.active_feature]}
											</Badge>
										)}
									</Box>
								);
							})}
						</Stack>
					)}
				</>
			)}
			<Group position="right" mt={24}>
				<Button variant="default" radius="md" onClick={handleDuplicateFunnelCloseModal} disabled={duplicating}>Cancel</Button>
				<Button radius="md" leftIcon={<IconCopy size={16} />} onClick={handleDuplicateFunnelSubmit} loading={duplicating} disabled={pagesLoading || checkedPages.length === 0}>Create Funnel</Button>
			</Group>
		</Modal>
	);
}
